import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import TooltipClose from '@/components/common/TooltipCloseSvg';
import { TmFavourite } from '@/tscapi/model/favourite';
import http from '@/tscapi/service/httpservice';
import { Cookie } from '@/tscapi/utils/cookie';
import { FavouriteStorage } from '@/tscapi/utils/favourites-storage';

import FavoriteEmptyIcon from '../pdp/FavoriteEmptyIcon';
import FavoriteIcon from '../pdp/FavoriteIcon';
import { IProduct, IEdp } from '../v2/Prp/IProduct';

import { en, fr } from './i18n';

export interface FavoriteProps {
    product: any;
    edp: any;
}
type FavoriteEventDetail = {
    productData: IProduct;
    selectedEdp: number;
};
function Favorite({ product, edp }: FavoriteProps) {
    const { i18n, t } = useTranslation('shoppingbag');
    i18n.addResourceBundle('en', 'shoppingbag', en, true, true);
    i18n.addResourceBundle('fr', 'shoppingbag', fr, true, true);
    const [isFavourite, setIsFavourite] = useState(false);
    const [favourites, setFavourites] = useState([]);
    const [showFavoriteTooltip, setShowFavoriteTooltip] = useState(false);
    const [currentIsFavorite, setCurrentIsFavorite] = useState(false);
    const FAVORITE_TOOLTIP_CLOSE_TIMEOUT = 3000;
    const productData = product;
    const cookie: Cookie = new Cookie();

    const isSignedIn = useMemo(() => {
        const cookie = new Cookie();
        return !!cookie.UserAccessToken;
    }, []);

    const onFavoriteTooltipCloseClick = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
        e.preventDefault();
        setShowFavoriteTooltip(false);
    };
    const handleSignInLink = (e) => {
        e.preventDefault();
        cookie.TargetUrl = window.location.pathname + window.location.search;
        window.location.href = '/pages/signin';
    };

    const onFavoriteClick = async () => {
        if (!isSignedIn) {
            setShowFavoriteTooltip(true);
            // setTimeout(() => {
            //     setShowFavoriteTooltip(false);
            // }, FAVORITE_TOOLTIP_CLOSE_TIMEOUT);
            return;
        }

        const uat = new Cookie().UserAccessToken;

        if (!uat) {
            setShowFavoriteTooltip(true);
            setTimeout(() => {
                setShowFavoriteTooltip(false);
            }, FAVORITE_TOOLTIP_CLOSE_TIMEOUT);
            return;
        }

        const userEdp = uat.Edp;

        if (!userEdp) {
            setShowFavoriteTooltip(true);
            setTimeout(() => {
                setShowFavoriteTooltip(false);
            }, FAVORITE_TOOLTIP_CLOSE_TIMEOUT);
            return;
        }

        const base = '/api/v2/';
        const lang = new Cookie().Language.substring(0, 2) || 'en';
        const item = edp;
        const deleteUrl = base + lang + '/accounts' + `/${userEdp}` + `/favourites/${item.ItemNo}`;

        const cachebleHeaders = {
            'Content-Type': 'application/json',
            'cache-control': 'no-cache',
            currentdatetime: new Cookie().CurrentDate,
        };
        const favouriteStorage = new FavouriteStorage();
        if (isFavourite) {
            try {
                await http.delete(deleteUrl, {
                    headers: cachebleHeaders,
                });
                favouriteStorage.removeByItemNumber(item.ItemNo);
                setIsFavourite(false);
            } catch (e) {}
        } else {
            try {
                const postUrl = base + lang + '/accounts' + `/${userEdp}` + '/favourites';
                await http.post(
                    postUrl,
                    {
                        ItemNumber: item.ItemNo,
                        ActivityDateTime: new Date().toISOString(),
                    },
                    {
                        headers: cachebleHeaders,
                    },
                );
                const favourite: TmFavourite = new TmFavourite();
                favourite.ItemNumber = item.ItemNo;
                favouriteStorage.add(favourite);
                setIsFavourite(true);
                //trigger TT analytics event
                const event = new CustomEvent<FavoriteEventDetail>('favoriteEvent', {
                    detail: { productData: productData, selectedEdp: edp.EdpNo },
                });
                document.dispatchEvent(event);
            } catch (e) {
                console.log('Failed to add favourite', e);
            }
        }
        setCurrentIsFavorite((prev) => !prev);
        return;
    };

    useEffect(() => {
        const getUserFavourites = () => {
            const favouriteStorage = new FavouriteStorage();
            const favList = favouriteStorage.Favourites;
            const foundFav = favList.find((item) => item.ItemNumber == productData.ItemNo);
            setFavourites(favouriteStorage.Favourites);
            if (foundFav) {
                setIsFavourite(true);
            }
        };
        // console.count('gf');
        getUserFavourites();
    }, [productData]);

    return (
        <div className="bag-items__favorite">
            <span className="bag-items__favorite__text">
                {isFavourite ? t('addedToFavourite') : t('addToFavourite')}
            </span>
            <button
                className="bag-items__favorite__icon-wrapper"
                type="button"
                aria-label="Add to Favorite"
                onClick={onFavoriteClick}
            >
                {isFavourite ? (
                    <FavoriteIcon className="bag-items__favorite__icon-self" />
                ) : (
                    <FavoriteEmptyIcon className="bag-items__favorite__icon-self" />
                )}
            </button>
            {showFavoriteTooltip && (
                <>
                    <div className="bag-items__favorite__overlay" onClick={(e) => onFavoriteTooltipCloseClick(e)}></div>
                    <div className="bag-items__favorite__tooltip">
                        <div className="bag-items__favorite__tooltip-content">
                            <span className="bag-items__favorite__tooltip-content__text">
                                {t('Please') + ' '}
                                <a
                                    className="bag-items__favorite__tooltip__sign-in"
                                    href="/pages/signin"
                                    key={1}
                                    onClick={(e) => {
                                        handleSignInLink(e);
                                    }}
                                >
                                    <b>{t('sign in')}</b>
                                </a>
                                /
                                <a
                                    className="bag-items__favorite__tooltip__register"
                                    href="/pages/createaccount"
                                    key={2}
                                >
                                    <b>{t('register')}</b>
                                </a>
                                {' ' + t('to add items to your Favorites list.')}
                            </span>
                            <button
                                onClick={(e) => onFavoriteTooltipCloseClick(e)}
                                className="bag-items__favorite__tooltip-content__close-button"
                            >
                                <span className="visually-hidden">{t('closeText')}</span>
                                <TooltipClose />
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default Favorite;
